import {
  COLORES_SEMBRADOS,
  esHerenciaDeOtraMarca,
  MARCAS,
  MarcaId,
  PaqueteMarca,
  PAQUETES_MARCA,
  resolverMarca,
} from './brand-packs';
import { DEFAULT_UI_CONFIG } from './ui-config.defaults';
import { UiBranding, UiConfig, UiConfigWire } from './ui-config.model';

/** Cadena utilizable: descarta `null`, `undefined` y cadenas en blanco. */
function texto(valor: string | undefined | null, porDefecto: string): string {
  const limpio = typeof valor === 'string' ? valor.trim() : '';
  return limpio.length > 0 ? limpio : porDefecto;
}

/**
 * Pasa el payload del cable a la forma que consume la app.
 *
 * Regla del contrato: **todo campo puede venir vacío y vacío significa "usa el
 * tuyo"**. "El tuyo" es el paquete de la marca que resuelve la edición. Lo
 * sembrado para otra marca (la fuente, los colores y el título con que nació
 * Consiglia) también cuenta como vacío.
 *
 * Logos, favicon e icono de aplicación salen SIEMPRE del paquete: los dos manuales
 * exigen usar el logo sólo desde sus archivos, y las URLs que algunas ediciones
 * guardaban (PNG viejos de otra versión del logo) se veían deformadas. Los campos
 * `*_url` del backend se ignoran.
 */
export function normalizeUiConfig(
  wire: UiConfigWire | null | undefined,
): UiConfig {
  if (!wire) return DEFAULT_UI_CONFIG;

  const base = DEFAULT_UI_CONFIG;
  const marca = wire.branding ?? {};
  const nombreProducto = texto(
    wire.edicion?.nombre_producto,
    base.edicion.nombreProducto,
  );
  const codigo = texto(wire.edicion?.codigo, base.edicion.codigo);

  const marcaId = resolverMarca(marca.marca, codigo, nombreProducto);
  const paquete = PAQUETES_MARCA[marcaId];

  /** Valor propio de la edición, o vacío si es herencia de otra marca. */
  const propio = (
    valor: string | undefined,
    obtener: (otro: PaqueteMarca) => string,
  ): string => {
    const limpio = texto(valor, '');
    return esHerenciaDeOtraMarca(marcaId, limpio, obtener) ? '' : limpio;
  };
  const branding: UiBranding = {
    marca: marcaId,
    logoUrl: paquete.horizontal.color,
    logoDarkUrl: paquete.horizontal.negativo,
    faviconUrl: paquete.favicon,
    appIconUrl: paquete.appIcon,
    colores: normalizarColores(marca.colores, marcaId),
    fuente: propio(marca.fuente, (otro) => otro.fuente) || paquete.fuente,
    // Si la edición no fija título de navegador, el nombre del producto es mejor
    // fallback que el nombre de la marca: es lo que el cliente compró.
    tituloNavegador:
      propio(marca.titulo_navegador, (otro) => otro.nombre) || nombreProducto,
  };

  return {
    configVersion: Number.isFinite(wire.config_version)
      ? Number(wire.config_version)
      : base.configVersion,
    edicion: { codigo, nombreProducto },
    branding,
    features: wire.features ?? {},
    textos: normalizarTextos(wire.textos),
  };
}

/**
 * Los colores son un objeto libre (jsonb). Se conservan sólo las entradas con un
 * valor utilizable y que no sean la paleta sembrada de otra marca. Lo ausente cae
 * a la paleta de la marca en `src/styles.css`, que distingue tema claro y oscuro.
 */
function normalizarColores(
  colores: Record<string, string> | null | undefined,
  marca: MarcaId,
): Record<string, string> {
  if (!colores) return {};

  const resultado: Record<string, string> = {};
  for (const [clave, valor] of Object.entries(colores)) {
    if (typeof valor !== 'string') continue;
    const limpio = valor.trim();
    if (limpio.length === 0) continue;

    const sembradoAjeno = MARCAS.some(
      (otra) =>
        otra !== marca &&
        COLORES_SEMBRADOS[otra][clave]?.toLowerCase() === limpio.toLowerCase(),
    );
    if (!sembradoAjeno) resultado[clave] = limpio;
  }
  return resultado;
}

/** Descarta claves vacías: un texto en blanco debe caer al fallback compilado. */
function normalizarTextos(
  textos: Record<string, string> | null | undefined,
): Record<string, string> {
  if (!textos) return {};

  const resultado: Record<string, string> = {};
  for (const [clave, valor] of Object.entries(textos)) {
    if (typeof valor !== 'string') continue;
    if (valor.trim().length === 0) continue;
    resultado[clave] = valor;
  }
  return resultado;
}
